import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Servicio auxiliar para construir rutas con prefijos de idioma
 * Simplifica la navegación manteniendo el idioma actual
 */
@Injectable({
    providedIn: 'root'
})
export class RouteHelperService {
    private router = inject(Router);

    /**
     * Obtiene el idioma actual desde la URL
     */
    getCurrentLanguage(): string {
        const currentUrl = this.router.url;
        const langMatch = currentUrl.match(/^\/(es|en)(\/|$)/);
        return langMatch ? langMatch[1] : 'es';
    }

    /**
     * Construye una ruta con el prefijo de idioma actual
     * @param path - Ruta relativa sin prefijo de idioma (ej: 'for-who', 'professionals')
     * @returns Ruta completa con prefijo de idioma (ej: '/es/for-who')
     */
    buildRoute(path: string): string {
        const lang = this.getCurrentLanguage();
        // Eliminar slash inicial si existe
        const cleanPath = path.startsWith('/') ? path.substring(1) : path;
        // Construir ruta con prefijo de idioma
        return `/${lang}/${cleanPath}`;
    }

    /**
     * Construye una ruta con array de segmentos
     * @param segments - Segmentos de la ruta (ej: ['professionals', 'details', '123'])
     * @returns Array con prefijo de idioma (ej: ['es', 'professionals', 'details', '123'])
     */
    buildRouteSegments(...segments: string[]): string[] {
        const lang = this.getCurrentLanguage();
        return [lang, ...segments];
    }

    /**
     * Navega a una ruta manteniendo el idioma actual
     * @param path - Ruta relativa sin prefijo de idioma
     */
    navigate(path: string): Promise<boolean> {
        const fullRoute = this.buildRoute(path);
        return this.router.navigateByUrl(fullRoute);
    }

    /**
     * Navega usando segmentos de ruta
     * @param segments - Segmentos de la ruta
     */
    navigateBySegments(...segments: string[]): Promise<boolean> {
        const routeSegments = this.buildRouteSegments(...segments);
        return this.router.navigate(routeSegments);
    }
}
